import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import {
  DEFAULT_MOBILE_SCREEN_SIZE,
  DEFAULT_SCREEN_SIZE,
  STORYBOARD_TYPES,
} from "@/utils/constants";
import { ReactFramework } from "@/utils/templateProjects";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import useLayoutStore, { LayoutMode } from "@/store/zustand/layoutStore";
import {
  useActiveMainCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { useInitializeStoryboard } from "@/hooks/storyboards/useInitializeStoryboards";
import { useCreateStoryboard } from "@/hooks/storyboards/useCreateStoryboard";
import { track } from "@/utils/events/tracker";
import type { Storyboard } from "@/utils/types";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { Canvas as CanvasType } from "@/utils/types";
import { StoryboardDelta } from "@/store/zustand/changeLedgerTypes";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { usePagesStore } from "@/store/zustand/pagesStore";

export const useMaybeInitializeMainStoryboard = () => {
  const mainCanvas = useActiveMainCanvas();
  const project = useActiveProject();
  const canvasId = useCanvasId();
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const dispatch = useDispatch<any>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initializeStoryboard = useInitializeStoryboard();
  const createStoryboard = useCreateStoryboard();
  const selectedPageId = usePagesStore(
    (state) => state.selectedPageId[canvasId],
  );
  const pages = usePagesStore((state) => state.pages[canvasId] || []);
  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage,
  );
  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const componentId = searchParams.get("componentId");
  const prompt = searchParams.get("prompt");

  const [isCompStoryboardCreated, setIsCompStoryboardCreated] =
    useState<boolean>(false);
  const [layoutUpdated, setLayoutUpdated] = useState(false);

  useEffect(() => {
    if (!mainCanvas || !project || !supabaseClient) {
      return;
    }

    if (mainCanvas.id !== canvasId) {
      return;
    }

    // Only auto-create storyboards if:
    // - No pages exist (legacy setup), OR
    // - Exactly one page exists (initial page that might need initialization)
    // If there are multiple pages, user is actively using pages feature and we shouldn't auto-create
    if (pages.length > 1) {
      return;
    }

    if (!componentId) {
      // Create only if there are no storyboards & there was never an application storyboard (that was deleted)
      if (
        mainCanvas.storyboards?.filter((sb) => !sb.deleted)?.length ||
        // User explicitly deleted, don't re-create
        mainCanvas.storyboards?.find(
          (sb) => sb.type === STORYBOARD_TYPES.APPLICATION && sb.deleted,
        )
      ) {
        return;
      }
    } else {
      if (mainCanvas.storyboards?.filter((sb) => !sb.deleted)?.length) {
        return;
      }

      /**
       * Set the default view to Design mode when a component is opened
       * from the ReactComponents page
       */
      if (!layoutUpdated) {
        // @ts-expect-error TS2345
        setNewLayout(null, LayoutMode.Design);
        setLayoutUpdated(true);
      }
    }

    const createMainStoryboard = async () => {
      // Storyboard has not been created
      let initialSize;
      if (project?.framework === ReactFramework.EXPO) {
        initialSize = DEFAULT_MOBILE_SCREEN_SIZE;
      } else {
        initialSize = DEFAULT_SCREEN_SIZE;
      }

      const newId = uuidv4().toString();
      const storyboardDeltas: StoryboardDelta[] = [
        {
          storyboardId: newId,
          changeType: "upsert",
          pageId: selectedPageId || "",
          storyboardUpdateDict: {
            id: newId,
            type: STORYBOARD_TYPES.APPLICATION,
            width: initialSize.width,
            height: initialSize.height,
            x: -initialSize.width / 2,
            y: -initialSize.height / 2,
            zIndex: 0,
            fitCanvasToStoryboard: 0.6,
          },
        },
      ];

      updateCanvasStoryboards(mainCanvas.id, storyboardDeltas);
    };

    /**
     * Create a new storyboard for a component while opening
     * from ReactComponents
     */
    const createComponentStoryboard = async () => {
      if (typeof componentId !== "string") {
        return;
      }

      const componentIdInteger = Number.parseInt(componentId);

      if (Number.isNaN(componentIdInteger)) {
        return;
      }

      // Double check canvas has no storyboards
      const { data: canvasData } = await supabaseClient
        .from("canvases")
        .select("*")
        .eq("id", canvasId)
        .single();

      if (!canvasData?.storyboards) {
        console.log("No storyboards in component canvas, returning");
        return;
      }

      const { data, error } = await supabaseClient
        .schema("reactcomponents")
        .from("components")
        .select("*")
        .eq("id", componentIdInteger)
        .single();

      if (error) {
        console.error("Error: Couldn't find component", error);
        toast.error("Couldn't find component");
        return;
      }

      if (!data) {
        toast.error("Couldn't find component");
        return;
      }

      try {
        const initialSize = DEFAULT_SCREEN_SIZE;
        const x = -initialSize.width / 2;
        const y = -initialSize.height / 2;
        // Create a new storyboard
        const newStoryboard = initializeStoryboard(x, y, 1200, 800);
        await createStoryboard({
          storyboard: newStoryboard,
          pageId: selectedPageId || "",
          fileContentsToCreateWith: data.contents,
          actionText: "React Component",
        });

        setIsCompStoryboardCreated(true);
        if (prompt && typeof prompt === "string") {
          const updatedPrompt = `Apply this change to the code in ${newStoryboard.name}: ${prompt}`;
          track("AI_PROMPT_REQUESTED", {
            type: "AI_CHANGE_FROM_REACTCOMPONENTS",
            prompt: updatedPrompt,
          });
          setSendChatMessage({
            sendChatMessage: updatedPrompt,
            resetToggles: "all",
          });
        }

        setSearchParams((prev) => {
          const newParams = {
            ...prev,
          };
          delete newParams["prompt"];
          delete newParams["componentId"];
          return newParams;
        });
      } catch (error) {
        console.error("Couldn't create a new storyboard: ", error);
        toast.error("Couldn't create a new storyboard: " + error);
      }
    };

    if (!componentId) {
      createMainStoryboard();
    } else if (!isCompStoryboardCreated) {
      createComponentStoryboard();
    }
  }, [
    mainCanvas?.storyboards,
    mainCanvas,
    project,
    componentId,
    prompt,
    supabaseClient,
    canvasId,
    dispatch,
    initializeStoryboard,
    createStoryboard,
    setSendChatMessage,
    setSearchParams,
    isCompStoryboardCreated,
    layoutUpdated,
    pages.length,
  ]);

  return {
    isCompStoryboardCreated,
  };
};
